import React, { forwardRef, useRef } from 'react';
import { styled } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { WorkloadAnalyticsPopover } from 'components/molecules/Popover';
import { Box } from 'shell/components';
import { DrawArc, findCenter } from 'utils/helper';

const WorkloadAnalyticsPieChart = forwardRef(({ selected, students, stats, onClick, ...rest }, ref) => {
    students = students.map((student) => ({ ...student, ...stats[student.owner_account_id] }));

    const theme = useTheme();
    const svgRef = useRef(null);

    const data = students.filter(({ totalPoints }) => totalPoints);
    const studentTotalPointsSum = students.reduce((acc, cur) => acc + cur.totalPoints, 0);

    const gap = !data.length ? 0 : 1;
    const graphRadius = 12;
    const strokeWidth = 0.3 * graphRadius;

    const angles = [0];
    if (!data.length) {
        angles.push(0);
    } else if (data.length === 1) {
        angles.push(359);
    } else {
        let pointer = 0;
        data.forEach((student) => {
            const angle = (Number(student.contributionPercentage) / 100) * 360;
            angles.push(pointer + angle);
            pointer += angle;
        });
    }

    const renderPopover = () => {
        const index = data.findIndex(({ owner_account_id }) => owner_account_id === selected);
        if (index < 0) return null;
        const student = data[index];
        if (!student.totalPoints) return null;
        const centerCoords = findCenter(graphRadius, strokeWidth, (angles[index] + angles[index + 1]) / 2);
        const adjustedCoords = {
            x: (centerCoords.x / 24) * svgRef.current.clientWidth,
            y: (centerCoords.y / 24) * svgRef.current.clientHeight,
        };
        return (
            <PopoverBox x={adjustedCoords.x} y={adjustedCoords.y}>
                <TriangleBox />
                <WorkloadAnalyticsPopover student={student} />
            </PopoverBox>
        );
    };

    return (
        <CustomBox ref={ref} {...rest}>
            <CustomSVG ref={svgRef} width={'24'} height={'24'} viewBox={'-3 -3 30 30'}>
                {studentTotalPointsSum === 0 ? (
                    <DrawArc
                        radius={graphRadius}
                        startAngle={0}
                        endAngle={359}
                        strokeColor={theme.palette.ensightful.primary[50]}
                        strokeWidth={strokeWidth}
                    />
                ) : (
                    data.map((element, i) => (
                        <DrawArc
                            key={i}
                            radius={graphRadius}
                            startAngle={angles[i] + gap}
                            endAngle={angles[i + 1]}
                            strokeColor={
                                element.owner_account_id === selected
                                    ? theme.palette.ensightful.primary[500]
                                    : theme.palette.ensightful.primary[100]
                            }
                            strokeWidth={strokeWidth}
                            onClick={() => onClick(element.owner_account_id)}
                        />
                    ))
                )}
            </CustomSVG>
            {renderPopover()}
        </CustomBox>
    );
});

const CustomBox = styled(Box)(() => {
    return {
        position: 'relative',
    };
});

const CustomSVG = styled('svg')(() => {
    return {
        width: '100%',
        height: '100%',
    };
});

const PopoverBox = styled(Box)(({ x, y }) => {
    return {
        position: 'absolute',
        top: `${y}px`,
        left: `calc(${x}px - 4.5625rem)`,
    };
});

const TriangleBox = styled(Box)(({ theme }) => {
    return {
        position: 'absolute',
        zIndex: theme.zIndex.tooltip,
        borderRadius: '1px',
        width: '11px',
        height: '11px',
        backgroundColor: theme.palette.ensightful.shades[0],
        transform: 'translate(calc(4.5625rem - 5.5px), -5px) rotate(45deg)',
    };
});

export default WorkloadAnalyticsPieChart;
